import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Key, User, Shield, Trash2, Database } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyFilter, setAgencyFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'admin',
    agency_id: ''
  });
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchAgencies();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all users (not just admins)
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
    setLoading(false);
  };
  
  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/create-admin`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpen(false);
      setFormData({ full_name: '', email: '', password: '', role: 'admin', agency_id: '' });
      fetchUsers();
      alert(`${formData.role.charAt(0).toUpperCase() + formData.role.slice(1)} user created successfully!`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('Passwords do not match!');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${selectedUser.id}/change-password`, {
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newPassword = passwordData.new_password;
      const userEmail = selectedUser.email;
      
      setPasswordDialogOpen(false);
      setPasswordData({ new_password: '', confirm_password: '' });
      setSelectedUser(null);
      
      // Show password to admin so they can communicate it to the user
      alert(
        `✅ Password changed successfully!\n\n` +
        `📧 User: ${userEmail}\n` +
        `🔑 New Password: ${newPassword}\n\n` +
        `⚠️ Please communicate this password to the user securely.`
      );
      
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${selectedUser.id}`, {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || '',
        role: formData.role,
        agency_id: formData.agency_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditDialogOpen(false);
      setFormData({ full_name: '', email: '', phone: '', password: '', role: 'admin', agency_id: '' });
      setSelectedUser(null);
      fetchUsers();
      alert('User updated successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      full_name: user.full_name,
      email: user.email,
      phone: user.phone || '',
      password: '',
      role: user.role,
      agency_id: user.agency_id || ''
    });
    setEditDialogOpen(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setPasswordData({ new_password: '', confirm_password: '' });
    setPasswordDialogOpen(true);
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'admin':
        return '#FFD700';
      case 'agent':
        return '#4A5A8A';
      case 'agency':
        return '#C0C0C0';
      default:
        return '#B0B0B0';
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.full_name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      alert('User deleted successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCleanupEmails = async () => {
    if (!window.confirm('This will delete all orphaned emails, rejected users/agencies, and old verification records. Continue?')) {
      return;
    }
    
    setCleaningUp(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/cleanup-orphaned-emails`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCleanupResults(response.data.results);
      setCleanupDialogOpen(true);
      fetchUsers(); // Refresh user list
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cleanup emails');
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) return <div style={{ color: '#B0B0B0' }}>Loading...</div>;

  return (
    <div data-testid="admin-users-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>User Management</h1>
          <p style={{ color: '#B0B0B0' }}>Manage users, roles, and permissions</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={handleCleanupEmails}
            disabled={cleaningUp}
            style={{ background: '#EF4444', color: '#FFFFFF' }}
          >
            <Database className="mr-2 h-4 w-4" />
            {cleaningUp ? 'Cleaning...' : 'Cleanup Emails'}
          </Button>
          <Button 
            onClick={() => setOpen(true)}
            style={{ background: '#FFFFFF', color: '#0A1128' }}
          >
            <Plus className="mr-2 h-4 w-4" />Create User
          </Button>
        </div>
      </div>
      
      {/* Create/Edit User Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#FFFFFF' }}>Create New User</DialogTitle>
              <DialogDescription style={{ color: '#B0B0B0' }}>Add a new user account with role</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Full Name</Label>
                <Input 
                  value={formData.full_name} 
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} 
                  required 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Email</Label>
                <Input 
                  type="email" 
                  value={formData.email} 
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                  required 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Password</Label>
                <Input 
                  type="password" 
                  value={formData.password} 
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                  required 
                  minLength={6}
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Minimum 6 characters</p>
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value, agency_id: value === 'agency' ? '' : '' })}>
                  <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    <SelectItem value="admin" style={{ color: '#E8E8E8' }}>Admin (System Administrator)</SelectItem>
                    <SelectItem value="staff" style={{ color: '#E8E8E8' }}>Staff (Company Employee)</SelectItem>
                    <SelectItem value="agency" style={{ color: '#E8E8E8' }}>Agency (External Partner)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                  Note: Create agents separately in Agents page for duty assignment
                </p>
              </div>
              
              {/* Show agency selector only when agency role is selected */}
              {formData.role === 'agency' && (
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Agency *</Label>
                  <Select value={formData.agency_id} onValueChange={(value) => setFormData({ ...formData, agency_id: value })}>
                    <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue placeholder="Select an agency..." />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      {agencies.filter(a => a.is_active !== false).map((agency) => (
                        <SelectItem key={agency.id} value={agency.id} style={{ color: '#E8E8E8' }}>
                          {agency.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                    Required for agency users - links user to specific agency
                  </p>
                </div>
              )}
              
              <Button type="submit" className="w-full" style={{ background: '#FFFFFF', color: '#0A1128' }}>
                Create User
              </Button>
            </form>
          </DialogContent>
        </Dialog>

      {/* Admin Users Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFD700' }}>
          <Shield className="inline-block mr-2 h-6 w-6" />
          Admin Users
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.filter(u => u.role === 'admin').map((user) => (
            <Card key={user.id} style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: '#FFD700' }}>
                  <Shield className="h-5 w-5" />
                  {user.full_name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Email</p>
                    <p style={{ color: '#E8E8E8' }}>{user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Phone</p>
                    <p style={{ color: '#E8E8E8' }}>{user.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Role</p>
                    <div className="inline-block px-3 py-1 rounded-full text-sm font-bold" style={{ 
                      background: 'rgba(255, 215, 0, 0.2)', 
                      color: '#FFD700',
                      border: '1px solid #FFD700'
                    }}>
                      Admin
                    </div>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Status</p>
                    <p style={{ color: user.status === 'approved' ? '#22C55E' : '#EF4444' }}>
                      {user.status || 'approved'}
                    </p>
                  </div>
                  <div className="flex gap-2 pt-3 border-t" style={{ borderColor: 'rgba(255, 215, 0, 0.2)' }}>
                    <Button size="sm" variant="ghost" onClick={() => openEditDialog(user)} style={{ color: '#60A5FA' }}>
                      <Edit className="h-4 w-4 mr-1" />Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openPasswordDialog(user)} style={{ color: '#FFD700' }}>
                      <Key className="h-4 w-4 mr-1" />Password
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(user)} style={{ color: '#FF6B6B' }}>
                      <Trash2 className="h-4 w-4 mr-1" />Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {users.filter(u => u.role === 'admin').length === 0 && (
          <p className="text-center py-8" style={{ color: '#B0B0B0' }}>No admin users found</p>
        )}
      </div>

      {/* Staff Users Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4" style={{ color: '#60A5FA' }}>
          <User className="inline-block mr-2 h-6 w-6" />
          Staff Users
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.filter(u => u.role === 'staff').map((user) => (
            <Card key={user.id} style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: '#60A5FA' }}>
                  <User className="h-5 w-5" />
                  {user.full_name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Email</p>
                    <p style={{ color: '#E8E8E8' }}>{user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Phone</p>
                    <p style={{ color: '#E8E8E8' }}>{user.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Role</p>
                    <div className="inline-block px-3 py-1 rounded-full text-sm font-bold" style={{ 
                      background: 'rgba(96, 165, 250, 0.2)', 
                      color: '#60A5FA',
                      border: '1px solid #60A5FA'
                    }}>
                      Staff
                    </div>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>Status</p>
                    <p style={{ color: user.status === 'approved' ? '#22C55E' : '#EF4444' }}>
                      {user.status || 'approved'}
                    </p>
                  </div>
                  <div className="flex gap-2 pt-3 border-t" style={{ borderColor: 'rgba(96, 165, 250, 0.2)' }}>
                    <Button size="sm" variant="ghost" onClick={() => openEditDialog(user)} style={{ color: '#60A5FA' }}>
                      <Edit className="h-4 w-4 mr-1" />Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openPasswordDialog(user)} style={{ color: '#FFD700' }}>
                      <Key className="h-4 w-4 mr-1" />Password
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(user)} style={{ color: '#FF6B6B' }}>
                      <Trash2 className="h-4 w-4 mr-1" />Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {users.filter(u => u.role === 'staff').length === 0 && (
          <p className="text-center py-8" style={{ color: '#B0B0B0' }}>No staff users found</p>
        )}
      </div>

      {/* Agency Users Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold" style={{ color: '#A855F7' }}>
            <User className="inline-block mr-2 h-6 w-6" />
            Agency Users
          </h2>
          <div className="w-64">
            <Label style={{ color: '#E8E8E8', fontSize: '0.875rem' }}>Filter by Agency</Label>
            <Select value={agencyFilter} onValueChange={setAgencyFilter}>
              <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                <SelectItem value="all">All Agencies</SelectItem>
                {agencies.filter(a => a.is_active !== false).map((agency) => (
                  <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users
            .filter(u => u.role === 'agency')
            .filter(u => agencyFilter === 'all' || u.agency_id === agencyFilter)
            .map((user) => {
              const userAgency = agencies.find(a => a.id === user.agency_id);
              return (
                <Card key={user.id} style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ color: '#A855F7' }}>
                      <User className="h-5 w-5" />
                      {user.full_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Email</p>
                        <p style={{ color: '#E8E8E8' }}>{user.email}</p>
                      </div>
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Phone</p>
                        <p style={{ color: '#E8E8E8' }}>{user.phone || '-'}</p>
                      </div>
                      {userAgency && (
                        <div>
                          <p className="text-sm" style={{ color: '#B0B0B0' }}>Agency</p>
                          <p style={{ color: '#A855F7' }}>{userAgency.name}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Role</p>
                        <div className="inline-block px-3 py-1 rounded-full text-sm font-bold" style={{ 
                          background: 'rgba(168, 85, 247, 0.2)', 
                          color: '#A855F7',
                          border: '1px solid #A855F7'
                        }}>
                          Agency
                        </div>
                      </div>
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Status</p>
                        <p style={{ color: user.status === 'approved' ? '#22C55E' : '#EF4444' }}>
                          {user.status || 'approved'}
                        </p>
                      </div>
                      <div className="flex gap-2 pt-3 border-t" style={{ borderColor: 'rgba(168, 85, 247, 0.2)' }}>
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(user)} style={{ color: '#60A5FA' }}>
                          <Edit className="h-4 w-4 mr-1" />Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openPasswordDialog(user)} style={{ color: '#FFD700' }}>
                          <Key className="h-4 w-4 mr-1" />Password
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(user)} style={{ color: '#FF6B6B' }}>
                          <Trash2 className="h-4 w-4 mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        {users.filter(u => u.role === 'agency').filter(u => agencyFilter === 'all' || u.agency_id === agencyFilter).length === 0 && (
          <p className="text-center py-8" style={{ color: '#B0B0B0' }}>No agency users found</p>
        )}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Edit User</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>Update user information and role</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Full Name</Label>
              <Input 
                value={formData.full_name} 
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} 
                required 
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
              />
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Email</Label>
              <Input 
                type="email" 
                value={formData.email} 
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                required 
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
              />
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Phone <span style={{ color: '#B0B0B0' }}>(optional)</span></Label>
              <Input 
                type="tel" 
                value={formData.phone || ''} 
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                placeholder="+32 123 456 789"
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
              />
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="admin" style={{ color: '#E8E8E8' }}>Admin (System Administrator)</SelectItem>
                  <SelectItem value="staff" style={{ color: '#E8E8E8' }}>Staff (Company Employee)</SelectItem>
                  <SelectItem value="agency" style={{ color: '#E8E8E8' }}>Agency (External Partner)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Show agency selector only when agency role is selected */}
            {formData.role === 'agency' && (
              <div>
                <Label style={{ color: '#E8E8E8' }}>Agency *</Label>
                <Select value={formData.agency_id} onValueChange={(value) => setFormData({ ...formData, agency_id: value })}>
                  <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                    <SelectValue placeholder="Select an agency..." />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    {agencies.filter(a => a.is_active !== false).map((agency) => (
                      <SelectItem key={agency.id} value={agency.id} style={{ color: '#E8E8E8' }}>
                        {agency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                  Required for agency users - links user to specific agency
                </p>
              </div>
            )}
            
            <Button type="submit" className="w-full" style={{ background: '#FFFFFF', color: '#0A1128' }}>
              Update User
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Change Password</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              {selectedUser ? `Change password for ${selectedUser.full_name}` : ''}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>New Password</Label>
              <Input 
                type="password" 
                value={passwordData.new_password} 
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })} 
                required 
                minLength={6}
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
              />
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Confirm Password</Label>
              <Input 
                type="password" 
                value={passwordData.confirm_password} 
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })} 
                required 
                minLength={6}
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
              />
            </div>
            <Button type="submit" className="w-full" style={{ background: '#FFFFFF', color: '#0A1128' }}>
              Change Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cleanup Results Dialog */}
      <Dialog open={cleanupDialogOpen} onOpenChange={setCleanupDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A', maxWidth: '600px' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#22C55E' }}>✅ Email Cleanup Completed</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Orphaned emails and inactive records have been cleaned from the database
            </DialogDescription>
          </DialogHeader>
          {cleanupResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded" style={{ background: 'rgba(239, 68, 68, 0.1)', borderLeft: '3px solid #EF4444' }}>
                  <p style={{ color: '#EF4444', fontSize: '0.875rem', fontWeight: '600' }}>Deleted Users</p>
                  <p style={{ color: '#FFFFFF', fontSize: '1.5rem', fontWeight: 'bold' }}>{cleanupResults.deleted_users}</p>
                </div>
                <div className="p-3 rounded" style={{ background: 'rgba(239, 68, 68, 0.1)', borderLeft: '3px solid #EF4444' }}>
                  <p style={{ color: '#EF4444', fontSize: '0.875rem', fontWeight: '600' }}>Deleted Agencies</p>
                  <p style={{ color: '#FFFFFF', fontSize: '1.5rem', fontWeight: 'bold' }}>{cleanupResults.deleted_agencies}</p>
                </div>
                <div className="p-3 rounded" style={{ background: 'rgba(251, 146, 60, 0.1)', borderLeft: '3px solid #FB923C' }}>
                  <p style={{ color: '#FB923C', fontSize: '0.875rem', fontWeight: '600' }}>Deleted Agents</p>
                  <p style={{ color: '#FFFFFF', fontSize: '1.5rem', fontWeight: 'bold' }}>{cleanupResults.deleted_agents}</p>
                </div>
                <div className="p-3 rounded" style={{ background: 'rgba(251, 146, 60, 0.1)', borderLeft: '3px solid #FB923C' }}>
                  <p style={{ color: '#FB923C', fontSize: '0.875rem', fontWeight: '600' }}>Deleted Verifications</p>
                  <p style={{ color: '#FFFFFF', fontSize: '1.5rem', fontWeight: 'bold' }}>{cleanupResults.deleted_email_verifications}</p>
                </div>
              </div>
              
              {cleanupResults.orphaned_emails && cleanupResults.orphaned_emails.length > 0 && (
                <div className="p-4 rounded" style={{ background: 'rgba(96, 165, 250, 0.1)', borderColor: '#60A5FA', border: '1px solid' }}>
                  <p style={{ color: '#60A5FA', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                    Orphaned Emails Cleaned ({cleanupResults.orphaned_emails.length})
                  </p>
                  <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {cleanupResults.orphaned_emails.map((email, idx) => (
                      <p key={idx} style={{ color: '#B0B0B0', fontSize: '0.75rem' }}>• {email}</p>
                    ))}
                  </div>
                </div>
              )}
              
              <Button 
                onClick={() => setCleanupDialogOpen(false)}
                className="w-full"
                style={{ background: '#22C55E', color: '#FFFFFF' }}
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
